import * as PIXI from 'pixi.js';
import { IPointZone } from 'js/bubbleCore/types';
import { IHexPixelSize } from 'js/bubbleCore/view/types';
import { spacePositionToPxPosition } from 'js/bubbleCore/view/utils/spacePositionToPxPosition';
import { Bubble } from 'js/bubbleCore/bubbles/types';
import { PoolGroup } from 'framework/utils/PoolGroup';
import { getColorForBubble } from 'js/bubbleCore/view/bubbles/getColorForBubble';
import { TweenManager } from 'framework/TweenManager/TweenManager';
import { Easing } from 'framework/TweenManager/Easing';
import { clamp } from 'framework/utils/math-utils';
import { Lerper } from 'framework/utils/Lerper';
import { Range } from 'framework/utils/Range';

export class PointZoneView extends PIXI.Container {
  private readonly alphaLerper: Lerper;
  private model: IPointZone;
  private readonly textPool: PoolGroup<PIXI.Text>;
  private readonly tweenManager: TweenManager;
  private text: PIXI.Text;

  constructor(
    model: IPointZone,
    textPool: PoolGroup<PIXI.Text>,
    tweenManager: TweenManager,
  ) {
    super();

    this.model = model;
    this.textPool = textPool;
    this.tweenManager = tweenManager;

    this.alphaLerper = new Lerper({
      initValue: 0.2,
      lerp: 0.3,
      autoUpdate: true,
      snap: 0.001,
      range: new Range(0.2, 1),
      destroySignal: this.onDestroyed,
    });
    this.alphaLerper.signals.onValueUpdate.add((value) => this.alpha = value);
    this.alphaLerper.signals.onUpdate.add(() => {
      this.alphaLerper.setTarget(this.alphaLerper.getTarget() - 0.02);
    });
    this.text = this.addChild(this.textPool.getInstance());
    this.text.text = this.model.score.toString();
  }

  public resize(model: IPointZone, pixelSize: IHexPixelSize): void {
    this.model = model;
    spacePositionToPxPosition(
      this.model.rect.x + this.model.rect.width * 0.5,
      this.model.rect.y,
      pixelSize,
      this.position as PIXI.Point
    );
  }

  public handleBubbleUpdate(bubble: Bubble): void {
    const activationDistance = 8;
    const rect = this.model.rect;

    if (
      bubble.hexSpaceX > rect.x &&
      bubble.hexSpaceX < rect.x + rect.width &&
      bubble.hexSpaceY > rect.y - activationDistance
    ) {
      const alphaTarget = clamp(
        1 - ((rect.y - bubble.hexSpaceY) / activationDistance), 0, 1,
      );
      if (alphaTarget > this.alphaLerper.getTarget()) {
        this.alphaLerper.setTarget(alphaTarget);
      }
    }
  }

  public async displayCollision(bubble: Bubble): Promise<void> {
    const text = this.textPool.getInstance();
    text.text = this.model.score.toString();
    text.style.fill = getColorForBubble(bubble);
    this.addChild(text);
    await this.tweenManager.create(text)
      .to({ y: '-100', alpha: 0, }, 500, Easing.Sinusoidal.Out, true)
      .asyncOnComplete()
    this.textPool.release(text);
  }
}